import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import StaffGate from "../components/staff/StaffGate";
import { api, errMsg, newIdemKey, money } from "../lib/staffApi";
import { AdminRefundQueue } from "../components/RefundRequests";

const input = "rounded-md border border-border bg-background px-2 py-1 text-sm";
const card = "mt-4 rounded-2xl border border-border bg-card p-4";
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

/* Asks for the reason every staff action needs (min 3 characters); null when cancelled. */
const askReason = (what) => {
  const r = window.prompt(`Reason for ${what} (saved in the audit log):`);
  if (r === null) return null;
  if (r.trim().length < 3) { window.alert("Please give a reason (at least 3 characters)."); return null; }
  return r.trim();
};

/* Money actions keep their idempotency key until they succeed or are abandoned: a retry of the SAME action reuses it. */
function useMoneyAction() {
  const pending = useRef(null);
  return useCallback(async (sig, send) => {
    if (!pending.current || pending.current.sig !== sig) pending.current = { sig, key: newIdemKey() };
    const res = await send(pending.current.key);
    pending.current = null;
    return res;
  }, []);
}

function Msg({ m }) {
  if (!m) return null;
  return <p className={`mt-2 text-sm ${m.err ? "text-red-600" : "text-emerald-700"}`} data-testid="admin-msg">{m.text}</p>;
}

function UsersTab({ s }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [m, setM] = useState(null);
  const money = useMoneyAction();
  const load = useCallback(() => api.get("/admin/users", { params: { q } }).then((r) => setRows(r.data)).catch((e) => setM({ err: 1, text: errMsg(e) })), [q]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => { setM(null); try { await fn(); setM({ text: ok }); load(); } catch (e) { setM({ err: 1, text: errMsg(e) }); } };
  const suspend = (u, on) => { const reason = askReason(on ? "suspending" : "unsuspending"); if (reason) act(() => api.post(`/admin/users/${u.user_id}/${on ? "suspend" : "unsuspend"}`, { reason }), on ? "Suspended; their sessions ended." : "Unsuspended."); };
  const setRole = (u, role) => { const reason = askReason(`changing the role to ${role}`); if (reason) act(() => api.post(`/admin/users/${u.user_id}/role`, { role, reason }), `Role set to ${role}; their sessions ended.`); };
  const reset2fa = (u) => { const reason = askReason("resetting 2FA"); if (reason) act(() => api.post(`/admin/users/${u.user_id}/2fa-reset`, { reason }), "2FA reset; they set it up again at /admin."); };
  const credits = (u) => {
    const raw = window.prompt("Run credits to add (negative to remove):", "10");
    const delta = parseInt(raw, 10);
    if (!raw || !delta) return;
    const reason = askReason(`${delta > 0 ? "adding" : "removing"} ${Math.abs(delta)} run credits`);
    if (!reason) return;
    act(() => money(`credits:${u.user_id}:${delta}:${reason}`, (idempotency_key) =>
      api.post(`/admin/users/${u.user_id}/run-credits`, { delta, reason, idempotency_key })), "Run credits updated.");
  };

  return (
    <div className={card}>
      <div className="flex gap-2">
        <input className={`${input} flex-1`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or user id" data-testid="admin-user-search" />
        <Button size="sm" variant="outline" onClick={load}>Search</Button>
      </div>
      <Msg m={m} />
      <table className="mt-3 w-full text-left text-xs">
        <thead><tr className="text-muted-foreground"><th className="py-1">User</th><th>Role</th><th>Status</th><th>Run credits</th><th /></tr></thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.user_id} className="border-t border-border align-top" data-testid={`admin-user-${u.user_id}`}>
              <td className="py-2">{u.name}<div className="text-muted-foreground">{u.email}</div><div className="font-mono text-[10px] text-muted-foreground">{u.user_id}</div></td>
              <td>{u.role || "member"}{u.totp_enabled ? " · 2FA" : ""}{u.org_verified ? " · verified org" : ""}</td>
              <td>{u.suspended ? "suspended" : "active"}{u.email_verified === false ? " · email unconfirmed" : ""}</td>
              <td>{u.run_credits || 0}</td>
              <td className="space-x-1 space-y-1 text-right">
                {s.can.support && <Button size="sm" variant="outline" onClick={() => suspend(u, !u.suspended)}>{u.suspended ? "Unsuspend" : "Suspend"}</Button>}
                {s.can.money && <Button size="sm" variant="outline" onClick={() => credits(u)}>Credits</Button>}
                {s.can.roles && (
                  <select className={input} value="" onChange={(e) => e.target.value && setRole(u, e.target.value)} aria-label="Change role">
                    <option value="">Role…</option>
                    {s.can.grant.map((r) => <option key={r} value={r}>{r === "user" ? "member" : r}</option>)}
                  </select>
                )}
                {s.can.roles && u.totp_enabled && <Button size="sm" variant="outline" onClick={() => reset2fa(u)}>Reset 2FA</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [m, setM] = useState(null);
  const moneyAct = useMoneyAction();
  const load = useCallback(() => api.get("/admin/payments", { params: { q } }).then((r) => setRows(r.data)).catch((e) => setM({ err: 1, text: errMsg(e) })), [q]);
  useEffect(() => { load(); }, [load]);
  const refund = async (p) => {
    const max = p.refundable_minor / 100;
    const raw = window.prompt(`Amount to refund (up to ${max.toFixed(2)} ${p.currency.toUpperCase()}). A full refund ends the Team time this payment bought.`, max.toFixed(2));
    if (raw === null) return;
    const amount_minor = Math.round(parseFloat(raw) * 100);
    if (!(amount_minor > 0)) return;
    const reason = askReason("this refund");
    if (!reason) return;
    setM(null);
    try {
      const r = await moneyAct(`refund:${p.provider}:${p.ref}:${amount_minor}:${reason}`, (idempotency_key) =>
        api.post("/admin/payments/refund", { provider: p.provider, ref: p.ref, amount_minor, reason, idempotency_key }));
      const d = r.data;
      setM({ text: `Refunded ${money(d.amount_minor, d.currency)}${d.team_ended || d.team_pass_ended ? "; Team access from this payment ended" : ""}${d.renewal_cancelled_at_razorpay === false ? ". Razorpay renewal NOT cancelled: cancel it in the Razorpay dashboard" : ""}.` });
      load();
    } catch (e) { setM({ err: 1, text: errMsg(e) }); }
  };
  return (
    <div className={card}>
      <div className="flex gap-2">
        <input className={`${input} flex-1`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="User id, Stripe session or Razorpay payment id" />
        <Button size="sm" variant="outline" onClick={load}>Search</Button>
      </div>
      <Msg m={m} />
      <table className="mt-3 w-full text-left text-xs">
        <thead><tr className="text-muted-foreground"><th className="py-1">Payment</th><th>User</th><th>Paid</th><th>Refunded</th><th /></tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={`${p.provider}:${p.ref}`} className="border-t border-border">
              <td className="py-2">{p.provider}<div className="font-mono text-[10px] text-muted-foreground">{p.ref}</div>{p.needs_refund && <div className="text-amber-700">duplicate charge: refund it</div>}</td>
              <td className="font-mono text-[10px]">{p.user_id}</td>
              <td>{money(p.amount_minor, p.currency)}<div className="text-muted-foreground">{when(p.paid_at)}</div></td>
              <td>{p.refunded_minor ? money(p.refunded_minor, p.currency) : "-"}</td>
              <td className="text-right">{p.refundable_minor > 0 && p.status === "paid" && <Button size="sm" variant="outline" onClick={() => refund(p)}>Refund</Button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TicketsTab() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [text, setText] = useState("");
  const [m, setM] = useState(null);
  const load = useCallback(() => api.get("/admin/tickets", { params: { status } }).then((r) => setRows(r.data)).catch((e) => setM({ err: 1, text: errMsg(e) })), [status]);
  useEffect(() => { load(); }, [load]);
  const send = async (t, extra) => {
    setM(null);
    try { const r = await api.post(`/admin/tickets/${t.id}/reply`, { message: text, ...extra }); setOpen(r.data); setText(""); load(); }
    catch (e) { setM({ err: 1, text: errMsg(e) }); }
  };
  return (
    <div className={card}>
      <select className={input} value={status} onChange={(e) => setStatus(e.target.value)}>
        {["open", "pending", "resolved", "closed", "all"].map((x) => <option key={x}>{x}</option>)}
      </select>
      <Msg m={m} />
      <ul className="mt-3 divide-y divide-border text-sm">
        {rows.map((t) => (
          <li key={t.id} className="py-2">
            <button className="text-left underline" onClick={() => setOpen(t)}>{t.subject}</button>
            <span className="ml-2 text-xs text-muted-foreground">{t.category} · {t.status} · {t.user_name} · {when(t.updated_at)}</span>
          </li>
        ))}
      </ul>
      {open && (
        <div className="mt-3 rounded-xl border border-border p-3 text-sm" data-testid="admin-ticket">
          <div className="font-semibold">{open.subject}</div>
          {open.messages.map((msg, i) => (
            <div key={i} className={`mt-2 rounded p-2 ${msg.internal ? "bg-amber-50" : msg.from === "staff" ? "bg-muted" : ""}`}>
              <div className="text-xs text-muted-foreground">{msg.from === "staff" ? `${msg.staff_name || "staff"}${msg.internal ? " (internal note)" : ""}` : "member"} · {when(msg.at)}</div>
              <div className="whitespace-pre-wrap">{msg.body}</div>
            </div>
          ))}
          <textarea className={`${input} mt-2 w-full`} rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply" />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" disabled={!text.trim()} onClick={() => send(open, {})}>Reply to member</Button>
            <Button size="sm" variant="outline" disabled={!text.trim()} onClick={() => send(open, { internal_note: true })}>Internal note</Button>
            <Button size="sm" variant="outline" onClick={() => send(open, { status: "resolved" })}>Mark resolved</Button>
            <Button size="sm" variant="outline" onClick={() => send(open, { status: "closed" })}>Close</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function VerifyTab() {
  const [rows, setRows] = useState([]);
  const [m, setM] = useState(null);
  const load = useCallback(() => api.get("/admin/verification-queue").then((r) => setRows(r.data)).catch((e) => setM({ err: 1, text: errMsg(e) })), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (r, d) => {
    const reason = askReason(d === "approve" ? "approving" : "rejecting");
    if (!reason) return;
    try { await api.post(`/admin/verification/${r.id}/${d}`, { reason }); setM({ text: d === "approve" ? "Approved." : "Rejected." }); load(); }
    catch (e) { setM({ err: 1, text: errMsg(e) }); }
  };
  return (
    <div className={card}>
      <Msg m={m} />
      {!rows.length && <p className="text-sm text-muted-foreground">Nothing waiting for review.</p>}
      {rows.map((r) => (
        <div key={r.id} className="mt-2 rounded-xl border border-border p-3 text-sm">
          <div className="font-semibold">{r.kind} · {r.user_name} <span className="font-mono text-[10px] text-muted-foreground">{r.user_id}</span></div>
          <div className="mt-1 whitespace-pre-wrap">{r.note}</div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => decide(r, "approve")}>Approve</Button>
            <Button size="sm" variant="outline" onClick={() => decide(r, "reject")}>Reject</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState("");
  const load = useCallback(() => api.get("/admin/audit-log", { params: { action } }).then((r) => setRows(r.data)).catch(() => setRows([])), [action]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className={card}>
      <input className={input} value={action} onChange={(e) => setAction(e.target.value)} placeholder="Filter by action, e.g. payment." />
      <p className="mt-2 text-xs text-muted-foreground">Every staff action, newest first. Nobody can edit or delete entries.</p>
      <table className="mt-2 w-full text-left text-xs">
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-border align-top">
              <td className="py-1 pr-2 text-muted-foreground">{when(a.created_at)}</td>
              <td className="pr-2">{a.admin_email} ({a.admin_role})</td>
              <td className="pr-2 font-mono">{a.action}</td>
              <td className="pr-2 font-mono text-[10px]">{a.target_id}</td>
              <td>{a.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OutboxTab() {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { api.get("/admin/sandbox/outbox").then((r) => setMsgs(r.data.messages)).catch(() => setMsgs([])); }, []);
  return (
    <div className={card}>
      <p className="text-xs text-muted-foreground">Sandbox: these emails were captured, not sent.</p>
      {msgs.map((x) => (
        <details key={x.id} className="mt-2 rounded border border-border p-2 text-sm">
          <summary>{when(x.created_at)} · {x.to} · {x.subject}</summary>
          <pre className="mt-1 whitespace-pre-wrap text-xs">{x.text}</pre>
        </details>
      ))}
    </div>
  );
}

function Console({ s }) {
  const tabs = [
    ["users", "Users", s.can.support || s.can.verify],
    ["tickets", "Tickets", s.can.support],
    ["verify", "Verification", s.can.verify],
    ["refund-requests", "Refund requests", s.can.money],
    ["payments", "Refunds", s.can.money],
    ["audit", "Audit log", s.can.audit_log],
    ["outbox", "Sandbox outbox", s.can.sandbox_outbox],
  ].filter((t) => t[2]);
  const [tab, setTab] = useState(tabs[0]?.[0]);
  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="admin-console">
      <Link to="/settings" className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Settings</Link>
      <h1 className="mt-3 font-display text-2xl font-semibold">Staff console</h1>
      <p className="text-xs text-muted-foreground">{s.user.email} · {s.role} · {s.app_env}{s.backup_codes_left < 3 ? ` · only ${s.backup_codes_left} backup codes left` : ""}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {tabs.map(([k, label]) => (
          <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}>{label}</Button>
        ))}
      </div>
      {tab === "users" && <UsersTab s={s} />}
      {tab === "tickets" && <TicketsTab />}
      {tab === "verify" && <VerifyTab />}
      {tab === "refund-requests" && <AdminRefundQueue />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "outbox" && <OutboxTab />}
    </div>
  );
}

export default function Admin() {
  return <StaffGate>{(s) => <Console s={s} />}</StaffGate>;
}
